import { useEffect, useState } from 'react';
import { useDataProvider } from 'react-admin';

/**
 * Spółki instancji — brane Z DANYCH, nie z listy w kodzie.
 *
 * Front miał w czterech miejscach wpisane na sztywno:
 *
 *   id === '272996' ? 'Marathon PL' : id === '297174' ? 'Marathon GmbH' : …
 *
 * CLAUDE.md zakazuje tego wprost („bez hardkodów `companyId`", konfiguracja
 * dwóch spółek sterowana danymi). Powód jest praktyczny, nie estetyczny:
 * numery należą do KONKRETNEGO klienta, więc każda kolejna instancja
 * wymagałaby edycji kodu frontu — a zadanie o czyszczeniu aplikacji mówi, żeby
 * takich miejsc nie zostawiać. Do tego przy zmianie numeru po stronie klienta
 * nazwa po cichu przestałaby się pokazywać.
 *
 * Spółek są dwie i zmieniają się raz na nigdy, więc pobieramy je RAZ i trzymamy
 * w pamięci modułu. To nie jest pamięć podręczna z wygasaniem — przeładowanie
 * strony i tak zaczyna od nowa.
 */

export interface Company {
  id: number;
  name: string;
  country: string | null;
  viaCompanyId: string | null;
}

let cached: Company[] | null = null;
let inFlight: Promise<Company[]> | null = null;

type ListProvider = {
  getList: (
    resource: string,
    options: unknown,
  ) => Promise<{ data: Company[] }>;
};

async function fetchCompanies(dataProvider: unknown): Promise<Company[]> {
  if (cached) return cached;
  // Kilka komponentów montuje się naraz; bez tego każdy wysłałby własne
  // zapytanie o te same dwa rekordy.
  if (inFlight) return inFlight;

  inFlight = (dataProvider as ListProvider)
    .getList('companies', {
      pagination: { page: 1, perPage: 50 },
      sort: { field: 'name', order: 'ASC' },
      filter: {},
    })
    .then(({ data }) => {
      cached = data;
      return data;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/** Czyści zapamiętane spółki — używane w testach. */
export function forgetCompanies(): void {
  cached = null;
  inFlight = null;
}

/**
 * Spółki instancji.
 *
 * Zwraca pustą listę, dopóki nie przyjdą — komponenty mają wtedy pokazać to,
 * co mają pod ręką (nazwę z rekordu), a nie migać pustym miejscem.
 */
export function useCompanies(): Company[] {
  const dataProvider = useDataProvider();
  const [companies, setCompanies] = useState<Company[]>(cached ?? []);

  useEffect(() => {
    let alive = true;
    void fetchCompanies(dataProvider)
      .then((s) => {
        if (alive) setCompanies(s);
      })
      .catch(() => {
        // Brak listy nie może wywrócić ekranu — komponent pokaże surowy
        // identyfikator zamiast nazwy.
        if (alive) setCompanies([]);
      });
    return () => {
      alive = false;
    };
  }, [dataProvider]);

  return companies;
}

/**
 * Nazwa spółki po jej identyfikatorze w komunikatach VIA.
 *
 * Gdy spółki jeszcze nie przyszły albo numeru nie ma na liście, oddajemy sam
 * numer. To uczciwsze niż myślnik: widać wtedy, że rekord wskazuje spółkę,
 * której nie znamy, zamiast sugerować, że pole jest puste.
 */
export function companyNameByViaId(
  companies: Company[],
  viaCompanyId?: string | null,
): string {
  if (!viaCompanyId) return '';
  const s = companies.find((x) => x.viaCompanyId === viaCompanyId);
  return s ? s.name : viaCompanyId;
}

/** Lista wyboru do filtrów — pusta, dopóki spółki nie przyjdą. */
export function companyChoices(
  companies: Company[],
): { id: string; name: string }[] {
  return companies
    .filter((s): s is Company & { viaCompanyId: string } => !!s.viaCompanyId)
    .map((s) => ({ id: s.viaCompanyId, name: s.name }));
}
